import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { createProductUpdatesRepository } from '../repositories/ProductUpdatesRepository/ProductUpdates.repository';
import { Rss } from 'lucide-react';

function WithActions({ data }) {
  return (
    <div className="flex flex-col w-full font-sans">
      <h3 className="text-zinc-800 text-sm font-semibold flex items-center gap-1">
        <Rss className="size-4 text-grey-700" /> {data.title}
      </h3>

      <div className="mt-2">
        <p className="text-sm">{data.description}</p>
      </div>
    </div>
  );
}

const ChangelogContext = createContext();

export const useChangelog = () => {
  const context = useContext(ChangelogContext);
  if (!context) {
    throw new Error('useChangelog must be used within a ChangelogProvider');
  }
  return context;
};

export const ChangelogProvider = ({ children }) => {
  const [lastUpdates, setLastUpdates] = useState(() => {
    const stored = localStorage.getItem('lastChangelogUpdates');
    if (stored) {
      return JSON.parse(stored);
    }
    // Initialize with default values for each type
    const defaultTimestamp = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    return {
      live: defaultTimestamp,
      beta: defaultTimestamp,
      alpha: defaultTimestamp
    };
  });

  const productUpdatesRepository = createProductUpdatesRepository();

  const fetchUpdates = useCallback(async (type = 'live') => {
    try {
      const updates = await productUpdatesRepository.fetchUpdatesByType(type, lastUpdates[type]);
      
      if (updates && updates.length > 0) {
        updates.forEach(update => {
          toast.info(WithActions, {
            data: {
              title: update.title,
              description: update.description,
            },
            ariaLabel: 'Message archived',
            className: 'w-[400px]',
            toastId: update._id,
            position: "bottom-right",
            autoClose: false,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            icon: false
          });
        });
        
        // Update last update timestamp for the specific type
        const newTimestamp = new Date().toISOString();
        const newLastUpdates = {
          ...lastUpdates,
          [type]: newTimestamp
        };
        localStorage.setItem('lastChangelogUpdates', JSON.stringify(newLastUpdates));
        setLastUpdates(newLastUpdates);
      }
    } catch (error) {
      console.error('Error fetching changelog updates:', error);
    }
  }, [lastUpdates, productUpdatesRepository]);

  const value = {
    fetchUpdates,
    lastUpdates,
  };

  return (
    <ChangelogContext.Provider value={value}>
      {children}
    </ChangelogContext.Provider>
  );
}; 